/**
 * ReapplyWorkflowSection Component
 *
 * UI component for the reapply workflow feature. Allows users to select scope options,
 * configure settings, preview affected transactions, and execute the workflow.
 *
 * Features:
 * - Scope selection: All, Import Config, or Date Range
 * - Multi-select dropdown for import configs (when import_config scope selected)
 * - DateRangePicker integration (when date_range scope selected)
 * - Include manually classified checkbox
 * - Preview calculation showing affected counts
 * - Confirmation dialog before execution
 * - Toast notifications for success/error feedback
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/pages/Accounting/shared/DateRangePicker';
import { useReapplyWorkflow } from '@/hooks/useReapplyWorkflow';
import type { ReapplyWorkflowScope, ReapplyPreviewRequest, ReapplyPreviewResult } from '@/types/accounting';

/**
 * Form state for the reapply workflow
 */
export interface ReapplyWorkflowFormState {
  scope: ReapplyWorkflowScope;
  selectedImportConfigIds: number[];
  dateRange: { start: string | null; end: string | null };
  includeManuallyClassified: boolean;
}

export interface ReapplyWorkflowSectionProps {
  /** Callback when form state changes */
  onFormStateChange?: (state: ReapplyWorkflowFormState) => void;
  /** Optional initial form state */
  initialState?: Partial<ReapplyWorkflowFormState>;
}

/**
 * Default form state
 */
const defaultFormState: ReapplyWorkflowFormState = {
  scope: 'all',
  selectedImportConfigIds: [],
  dateRange: { start: null, end: null },
  includeManuallyClassified: false,
};

/**
 * PreviewResultsPanel displays the preview calculation results
 */
interface PreviewResultsPanelProps {
  preview: ReapplyPreviewResult;
  onExecute: () => void;
  loading: boolean;
}

function PreviewResultsPanel({ preview, onExecute, loading }: PreviewResultsPanelProps) {
  const { transaction_count, entry_count, suggested_rule_count, auto_rule_count, import_configs } =
    preview.preview;

  // Handle edge case: no matching transactions
  if (transaction_count === 0) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="preview-no-transactions">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <p className="text-sm font-medium text-yellow-800">No matching transactions</p>
            <p className="mt-1 text-xs text-yellow-700">
              No transactions match the selected criteria. Try adjusting your scope or options.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid="preview-results">
      <div className="flex items-start gap-3">
        <svg
          className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-800">Preview Results</p>
          <div className="mt-2 space-y-2">
            {/* Main counts */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-blue-600 font-medium">{transaction_count.toLocaleString()}</span>
                <span className="text-blue-700">transaction{transaction_count !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-blue-600 font-medium">{entry_count.toLocaleString()}</span>
                <span className="text-blue-700">entr{entry_count !== 1 ? 'ies' : 'y'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-blue-600 font-medium">{suggested_rule_count.toLocaleString()}</span>
                <span className="text-blue-700">suggested rule{suggested_rule_count !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-blue-600 font-medium">{auto_rule_count.toLocaleString()}</span>
                <span className="text-blue-700">auto-rule{auto_rule_count !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Import configs breakdown */}
            {import_configs.length > 0 && (
              <div className="mt-3 pt-2 border-t border-blue-200">
                <p className="text-xs font-medium text-blue-700 mb-1.5">By Import Config:</p>
                <ul className="space-y-1">
                  {import_configs.map((config) => (
                    <li key={config.id} className="flex items-center justify-between text-xs">
                      <span className="text-blue-700">{config.name}</span>
                      <span className="text-blue-600 font-medium">
                        {config.transaction_count.toLocaleString()} txns
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Execute button */}
          <div className="mt-4">
            <button
              type="button"
              onClick={onExecute}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="reapply-workflow-button"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Reapplying...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Reapply Workflow
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ConfirmationDialog for confirming workflow execution
 */
interface ConfirmationDialogProps {
  preview: ReapplyPreviewResult;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function ConfirmationDialog({ preview, onConfirm, onCancel, loading }: ConfirmationDialogProps) {
  const { transaction_count, entry_count, suggested_rule_count, auto_rule_count } = preview.preview;

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      data-testid="confirmation-dialog-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="confirmation-dialog"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-orange-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Confirm Reapply Workflow</h2>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> This will delete all existing transaction entries,
              suggested rules, and auto-rules for the selected transactions. The raw import data
              will be reprocessed from scratch.
            </p>
          </div>

          <p className="text-sm text-gray-700 mb-3">The following will be affected:</p>

          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-gray-700">
                <strong className="text-gray-900">{transaction_count.toLocaleString()}</strong>{' '}
                transaction{transaction_count !== 1 ? 's' : ''} will be reset
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-gray-700">
                <strong className="text-gray-900">{entry_count.toLocaleString()}</strong>{' '}
                entr{entry_count !== 1 ? 'ies' : 'y'} will be deleted
              </span>
            </li>
            {suggested_rule_count > 0 && (
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-gray-700">
                  <strong className="text-gray-900">{suggested_rule_count.toLocaleString()}</strong>{' '}
                  suggested rule{suggested_rule_count !== 1 ? 's' : ''} will be deleted
                </span>
              </li>
            )}
            {auto_rule_count > 0 && (
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-gray-700">
                  <strong className="text-gray-900">{auto_rule_count.toLocaleString()}</strong>{' '}
                  auto-rule{auto_rule_count !== 1 ? 's' : ''} will be deleted
                </span>
              </li>
            )}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cancel-confirm-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-reapply-button"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Reapplying...
              </>
            ) : (
              'Confirm & Reapply'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ReapplyWorkflowSection renders the UI for configuring reapply workflow options.
 *
 * @example
 * ```tsx
 * <ReapplyWorkflowSection
 *   onFormStateChange={(state) => console.log('Form state:', state)}
 * />
 * ```
 */
export function ReapplyWorkflowSection({
  onFormStateChange,
  initialState,
}: ReapplyWorkflowSectionProps) {
  // Form state
  const [scope, setScope] = useState<ReapplyWorkflowScope>(
    initialState?.scope ?? defaultFormState.scope
  );
  const [selectedImportConfigIds, setSelectedImportConfigIds] = useState<number[]>(
    initialState?.selectedImportConfigIds ?? defaultFormState.selectedImportConfigIds
  );
  const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>(
    initialState?.dateRange ?? defaultFormState.dateRange
  );
  const [includeManuallyClassified, setIncludeManuallyClassified] = useState(
    initialState?.includeManuallyClassified ?? defaultFormState.includeManuallyClassified
  );

  // Dropdown state for import configs
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Use the reapply workflow hook
  const {
    importConfigs,
    loading,
    error,
    preview,
    previewReapply,
    executeReapply,
    getImportConfigs,
    clearPreview,
  } = useReapplyWorkflow();

  // Fetch import configs on mount
  useEffect(() => {
    getImportConfigs().catch(() => {
      // Error is already handled by the hook
    });
  }, [getImportConfigs]);

  // Build current form state
  const formState = useMemo<ReapplyWorkflowFormState>(
    () => ({
      scope,
      selectedImportConfigIds,
      dateRange,
      includeManuallyClassified,
    }),
    [scope, selectedImportConfigIds, dateRange, includeManuallyClassified]
  );

  // Notify parent of form state changes
  useEffect(() => {
    onFormStateChange?.(formState);
  }, [formState, onFormStateChange]);

  // Handle scope change
  const handleScopeChange = useCallback((newScope: ReapplyWorkflowScope) => {
    setScope(newScope);
    // Reset scope-specific fields when changing scope
    if (newScope !== 'import_config') {
      setSelectedImportConfigIds([]);
    }
    if (newScope !== 'date_range') {
      setDateRange({ start: null, end: null });
    }
  }, []);

  // Handle import config selection toggle
  const handleImportConfigToggle = useCallback((configId: number) => {
    setSelectedImportConfigIds((prev) => {
      if (prev.includes(configId)) {
        return prev.filter((id) => id !== configId);
      }
      return [...prev, configId];
    });
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((start: string | null, end: string | null) => {
    setDateRange({ start, end });
  }, []);

  // Get selected config names for display
  const selectedConfigNames = useMemo(() => {
    if (selectedImportConfigIds.length === 0) return 'Select import configs...';
    const names = importConfigs
      .filter((config) => selectedImportConfigIds.includes(config.id))
      .map((config) => config.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  }, [selectedImportConfigIds, importConfigs]);

  // Check if scope selection is valid for preview
  const isScopeValid = useMemo(() => {
    if (scope === 'all') return true;
    if (scope === 'import_config') return selectedImportConfigIds.length > 0;
    if (scope === 'date_range') return dateRange.start !== null && dateRange.end !== null;
    return false;
  }, [scope, selectedImportConfigIds, dateRange]);

  // Clear preview when form state changes
  useEffect(() => {
    if (preview) {
      clearPreview();
    }
    // Only depend on form state, not preview itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, selectedImportConfigIds, dateRange, includeManuallyClassified, clearPreview]);

  // Handle calculate preview
  const handleCalculatePreview = useCallback(async () => {
    if (!isScopeValid) return;

    const request = buildPreviewRequest(formState);
    try {
      await previewReapply(request);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to calculate preview');
    }
  }, [isScopeValid, formState, previewReapply]);

  // Handle execute workflow
  const handleExecuteWorkflow = useCallback(async () => {
    if (!preview || preview.preview.transaction_count === 0) return;

    const request = buildPreviewRequest(formState);
    try {
      const result = await executeReapply(request);
      setShowConfirmDialog(false);
      toast.success(result.message);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to execute workflow');
    }
  }, [preview, formState, executeReapply]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Card Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Re-apply Transaction Workflow</h2>
        <p className="mt-1 text-sm text-gray-500">
          Reprocess transactions from the raw import stage to apply updated parsing logic and
          classification rules.
        </p>
      </div>

      {/* Card Body */}
      <div className="px-6 py-5 space-y-6">
        {/* Error display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Scope Selection */}
        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-3">Scope</legend>
          <div className="space-y-3">
            {/* All transactions */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="radio"
                name="scope"
                value="all"
                checked={scope === 'all'}
                onChange={() => handleScopeChange('all')}
                className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                  All transactions
                </span>
                <p className="text-xs text-gray-500">
                  Reprocess all transactions from all import sources
                </p>
              </div>
            </label>

            {/* Specific import config */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="scope"
                  value="import_config"
                  checked={scope === 'import_config'}
                  onChange={() => handleScopeChange('import_config')}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                    Specific import config
                  </span>
                  <p className="text-xs text-gray-500">
                    Reprocess transactions from selected bank/account sources
                  </p>
                </div>
              </label>

              {/* Import config multi-select dropdown */}
              {scope === 'import_config' && (
                <div className="mt-3 ml-7">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      disabled={loading}
                      className="w-full px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded-md shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-haspopup="listbox"
                      aria-expanded={isDropdownOpen}
                      data-testid="import-config-dropdown"
                    >
                      <span
                        className={
                          selectedImportConfigIds.length === 0 ? 'text-gray-500' : 'text-gray-900'
                        }
                      >
                        {loading ? 'Loading...' : selectedConfigNames}
                      </span>
                      <svg
                        className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown options */}
                    {isDropdownOpen && (
                      <div
                        className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                        role="listbox"
                        aria-multiselectable="true"
                      >
                        {importConfigs.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No import configs available
                          </div>
                        ) : (
                          importConfigs.map((config) => (
                            <label
                              key={config.id}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              role="option"
                              aria-selected={selectedImportConfigIds.includes(config.id)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedImportConfigIds.includes(config.id)}
                                onChange={() => handleImportConfigToggle(config.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="flex-1 text-sm text-gray-900">{config.name}</span>
                              <span className="text-xs text-gray-500">
                                {config.transactionCount.toLocaleString()} txns
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Click outside handler */}
                  {isDropdownOpen && (
                    <div
                      className="fixed inset-0 z-0"
                      onClick={() => setIsDropdownOpen(false)}
                      aria-hidden="true"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Date range */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="scope"
                  value="date_range"
                  checked={scope === 'date_range'}
                  onChange={() => handleScopeChange('date_range')}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                    Date range
                  </span>
                  <p className="text-xs text-gray-500">
                    Reprocess transactions within a specific date range
                  </p>
                </div>
              </label>

              {/* DateRangePicker */}
              {scope === 'date_range' && (
                <div className="mt-3 ml-7">
                  <DateRangePicker
                    from={dateRange.start}
                    to={dateRange.end}
                    onChange={handleDateRangeChange}
                    placeholder="Select date range..."
                    className="w-full max-w-sm"
                    data-testid="reapply-date-range-picker"
                  />
                </div>
              )}
            </div>
          </div>
        </fieldset>

        {/* Divider */}
        <hr className="border-gray-200" />

        {/* Options */}
        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-3">Options</legend>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={includeManuallyClassified}
              onChange={(e) => setIncludeManuallyClassified(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              data-testid="include-manually-classified"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                Include manually classified transactions
              </span>
              <p className="text-xs text-gray-500">
                By default, manually classified transactions are preserved. Check this to
                reprocess them as well.
              </p>
            </div>
          </label>
        </fieldset>

        {/* Preview Results Panel */}
        {preview && (
          <PreviewResultsPanel
            preview={preview}
            onExecute={() => setShowConfirmDialog(true)}
            loading={loading}
          />
        )}
      </div>

      {/* Card Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={handleCalculatePreview}
          disabled={!isScopeValid || loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="calculate-preview-button"
        >
          {loading && !preview ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Calculating...
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              Calculate Preview
            </>
          )}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && preview && (
        <ConfirmationDialog
          preview={preview}
          onConfirm={handleExecuteWorkflow}
          onCancel={() => setShowConfirmDialog(false)}
          loading={loading}
        />
      )}
    </div>
  );
}

/**
 * Build a preview request from form state
 */
export function buildPreviewRequest(formState: ReapplyWorkflowFormState): ReapplyPreviewRequest {
  const request: ReapplyPreviewRequest = {
    scope: formState.scope,
    include_manually_classified: formState.includeManuallyClassified,
  };

  if (formState.scope === 'import_config' && formState.selectedImportConfigIds.length > 0) {
    request.import_config_ids = formState.selectedImportConfigIds;
  }

  if (
    formState.scope === 'date_range' &&
    formState.dateRange.start &&
    formState.dateRange.end
  ) {
    request.date_range = {
      start: formState.dateRange.start,
      end: formState.dateRange.end,
    };
  }

  return request;
}

export default ReapplyWorkflowSection;
